import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '~/actions';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Box } from '~/commonComponents';
import { DashboardJobsType, IGetDashboardJobsInfoParams } from '../dashboardJobsApi';
import {
  DASHBOARD_QUERY_TIMING,
  DASHBOARD_REFRESH_INTERVAL_MS,
} from '../dashboardJobsConstants';
import { dashboardJobsService } from '../dashboardJobsService';
import { IDashboardJobsInfo } from '../dashboardJobsTypes';

export function useGetDashboardJobInfo(params?: IGetDashboardJobsInfoParams) {
  const dispatch = useDispatch();

  const query = useQuery<IDashboardJobsInfo>({
    queryKey: ['dashboard-jobs-info', params?.jobType ?? DashboardJobsType.ALL],
    queryFn: () => dashboardJobsService.getDashboardJobsInfo(params),
    ...DASHBOARD_QUERY_TIMING,
    // A professional applying is a change nothing in this app pushes, and the only
    // other triggers are a remount and a window visibilitychange — so an office that
    // leaves the portal open never sees it (GOTU-8310). The timer only runs while the
    // tab is visible, not hidden (refetchIntervalInBackground is false by default, and
    // query-core gates the tick on focusManager.isFocused(), which is
    // `visibilityState !== 'hidden'` — a visible-but-unfocused window still polls).
    //
    // It stops entirely while the query is failing with nothing on screen: query-core's
    // fetchState resets status to 'pending' whenever it refetches with data === undefined,
    // so a fixed interval would flip isError false→true every minute and re-raise the
    // blocking modal below over and over. A remount or a tab switch still retries, and a
    // poll that fails *over existing data* keeps polling — that one is recoverable.
    refetchInterval: (query) =>
      query.state.status === 'error' && query.state.data === undefined
        ? false
        : DASHBOARD_REFRESH_INTERVAL_MS,
    retry: 1,
  });

  useEffect(() => {
    if (query.data) {
      dispatch({
        type: 'SET_DASHBOARD_INFO',
        payload: {
          scheduled: query.data.filledCount,
          actionRequired: query.data.pendingCount,
          activeRequest: query.data.openCount,
          pendingReviewJobCount: query.data.pendingReviewJobCount,
          todayShift: query.data.todayShiftCount,
        },
      });
    }
  }, [query.data, dispatch]);

  useEffect(() => {
    // Only when there is nothing on screen. A failed *background* poll still sets
    // status 'error' while the last good counts survive (query-core query.js), and a
    // blocking "please refresh" modal over working counts would be worse than the blip.
    if (query.isError && !query.data) {
      dispatch({
        type: actions.SHOW_MESSAGE,
        payload: {
          popupProps: {
            Icon: () => (
              <Box marginBottom={20}>
                <OutlineWarning01Icon size={80} color={colors.warning[500]} />
              </Box>
            ),
            rightButtonText: 'Close',
          },
          title: 'Oops! Something Went Wrong',
          message: (
            <span>
              We weren’t able to load your Shifts Overview.
              <br />
              Please refresh the page to try again.
            </span>
          ),
        },
      });
    }
  }, [query.isError, query.data, dispatch]);

  return query;
}
